import { useEffect, useRef, useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { INewPasswordFormModel } from "../models/newPasswordFormModel";
import { useNewPasswordForm } from "./useNewPasswordForm";
import { useNavigate, useParams } from "react-router-dom";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { useAppContext } from "../../../AppContext";
import { IUserModel } from "../../../common/infrastructure/hooks/useAuthentication";
import { useAuthenticationFunctions } from "../../authentication/utils/AuthenticationUtility";
import { IOperationResultViewModel } from "../../../common/models/operationResultViewModel";
import { getDefaultRoutePath } from "../../../common/infrastructure/routes/routePaths";
import { getErrorToast } from "../../../common/infrastructure/utilities/toastUtility";

interface IValidatePasswordTokenResult {
  isValid: boolean;
  phoneNumber?: string;
}

export const useNewPasswordPage = () => {
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
  const hasValidated = useRef(false);

  const { token, email, studioId } = useParams();

  const { isMobile, selectedStudioId } = useAppContext();
  const { onSuccessfulUserLogin } = useAuthenticationFunctions();

  const { mutate, mutateAsync } = useApiMutation();

  const decodedToken = decodeURIComponent(token ?? "");
  const decodedEmail = decodeURIComponent(email ?? "");
  const decodedStudioId = decodeURIComponent(studioId ?? "");

  const form = useNewPasswordForm(decodedEmail, decodedToken, decodedStudioId);

  useEffect(() => {
    if (hasValidated.current) {
      return;
    }

    const validateToken = async () => {
      hasValidated.current = true;

      try {
        const result = await mutateAsync({
          endpoint: endpointsMetadata.authentication.validatePasswordToken,
          method: "post",
          disableSuccessToast: true,
          globalIsLoading: false,
          payload: {
            token: decodedToken,
            email: decodedEmail,
            studioId: parseInt(decodedStudioId, 10),
          },
        });

        const validationResult = result as IValidatePasswordTokenResult;

        if (!validationResult.isValid) {
          getErrorToast("Този линк за възстановяване на парола не е валиден.");
          navigate(getDefaultRoutePath());
        } else {
          setPhoneNumber(validationResult.phoneNumber);
        }
      } catch {
        getErrorToast("Този линк за възстановяване на парола не е валиден.");
        navigate(getDefaultRoutePath());
      } finally {
        setIsValidating(false);
      }
    };

    // Wait for selectedStudioId to match the studioId from URL
    const urlStudioId = parseInt(decodedStudioId, 10);

    if (
      decodedToken &&
      decodedEmail &&
      decodedStudioId &&
      selectedStudioId === urlStudioId
    ) {
      validateToken();
    } else if (!decodedToken || !decodedEmail || !decodedStudioId) {
      hasValidated.current = true;
      setIsValidating(false);
      getErrorToast("Този линк за възстановяване на парола не е валиден.");
      navigate(getDefaultRoutePath());
    }
    // Note: If selectedStudioId !== urlStudioId, wait for it to be updated by useSelectStudio
  }, [
    decodedToken,
    decodedEmail,
    decodedStudioId,
    selectedStudioId,
    mutateAsync,
    navigate,
  ]);

  const submit: SubmitHandler<INewPasswordFormModel> = async (data) => {
    mutate(
      {
        endpoint: endpointsMetadata.authentication.setNewPassword,
        globalIsLoading: true,
        method: "put",
        payload: data,
      },
      {
        onSuccess: (data) => {
          const result = data as IOperationResultViewModel<IUserModel>;

          onSuccessfulUserLogin(result.additionalData);
        },
      },
    );
  };

  return { form, isMobile, submit, isValidating, phoneNumber };
};
